import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LogOut, Users, Calendar, Mail, Phone, MapPin, Download } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total_submissions: 0, remaining_spots: 100 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Please login to access admin dashboard');
      navigate('/admin/login');
      return;
    }

    fetchSubmissions();
    fetchStats();
  }, [navigate]);

  const fetchSubmissions = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${apiUrl}/api/waitlist/submissions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch submissions');
      }

      const data = await response.json();
      setSubmissions(data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error(error.message || 'Failed to load submissions');
      if (error.message.includes('login')) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_username');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${apiUrl}/api/waitlist/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleExportCSV = () => {
    if (submissions.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Prepare CSV data
    const headers = ['Name', 'Email', 'Phone', 'City', 'Submitted At'];
    const rows = submissions.map(sub => [
      sub.full_name,
      sub.email,
      sub.phone,
      sub.city,
      new Date(sub.created_at).toLocaleString()
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `baitho_waitlist_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('CSV exported successfully');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="font-serif text-2xl md:text-3xl font-bold" style={{ color: '#8a8635' }}>
                Baitho Admin
              </h1>
              <Badge className="bg-terracotta/10 text-terracotta border-0">
                {localStorage.getItem('admin_username')}
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="default"
                onClick={() => navigate('/')}
                className="border-2 border-gray-300"
              >
                View Site
              </Button>
              <Button
                variant="outline"
                size="default"
                onClick={handleLogout}
                className="border-2 border-red-300 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-2 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Submissions</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_submissions}</p>
                </div>
                <div className="bg-mustard/10 p-3 rounded-full">
                  <Users className="w-8 h-8 text-mustard" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Remaining Spots</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.remaining_spots}</p>
                </div>
                <div className="bg-terracotta/10 p-3 rounded-full">
                  <Calendar className="w-8 h-8 text-terracotta" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Fill Rate</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats.total_submissions > 0 ? Math.round((stats.total_submissions / 100) * 100) : 0}%
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submissions Table */}
        <Card className="border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-mustard/10 to-terracotta/10 border-b-2 border-gray-200">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-serif text-gray-900">
                Waitlist Submissions
              </CardTitle>
              <Button
                onClick={handleExportCSV}
                className="bg-mustard hover:bg-[#D4A43D] text-gray-900 font-semibold"
                disabled={submissions.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-mustard"></div>
                <p className="mt-4 text-gray-600">Loading submissions...</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No submissions yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-4 px-3 font-semibold text-gray-700">#</th>
                      <th className="text-left py-4 px-3 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-4 px-3 font-semibold text-gray-700">Email</th>
                      <th className="text-left py-4 px-3 font-semibold text-gray-700">Phone</th>
                      <th className="text-left py-4 px-3 font-semibold text-gray-700">City</th>
                      <th className="text-left py-4 px-3 font-semibold text-gray-700">Submitted At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission, index) => (
                      <tr key={submission.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-3 text-gray-600">{index + 1}</td>
                        <td className="py-4 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-mustard/20 rounded-full flex items-center justify-center">
                              <span className="text-sm font-semibold text-mustard">
                                {submission.full_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900">{submission.full_name}</span>
                          </div>
                        </td>
                        <td className="py-4 px-3">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {submission.email}
                          </div>
                        </td>
                        <td className="py-4 px-3">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {submission.phone}
                          </div>
                        </td>
                        <td className="py-4 px-3">
                          <div className="flex items-center gap-2 text-gray-700">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {submission.city}
                          </div>
                        </td>
                        <td className="py-4 px-3 text-gray-600 text-sm">
                          {formatDate(submission.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
